import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine, Dot } from "recharts";
import { formatCurrency } from "@/lib/businesscaseCalculations";

interface PaybackTimelineProps {
  yearlyData: Array<{
    year: number;
    cumulativeCashflow: number;
  }>;
  paybackPeriod: number | null;
}

export function PaybackTimeline({ yearlyData, paybackPeriod }: PaybackTimelineProps) {
  if (!yearlyData || yearlyData.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <span>⏱️</span>
            Payback Period Timeline
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex h-[300px] items-center justify-center text-muted-foreground">
            Geen data beschikbaar
          </div>
        </CardContent>
      </Card>
    );
  }

  // Find breakeven year (first year with positive cumulative cashflow)
  const breakevenYear = yearlyData.find((d) => d.cumulativeCashflow > 0);

  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      const distanceToBreakeven = data.cumulativeCashflow < 0 ? Math.abs(data.cumulativeCashflow) : 0;
      
      return (
        <div className="rounded-lg border bg-background p-3 shadow-lg">
          <p className="font-semibold">Jaar {data.year}</p>
          <p className={`text-lg ${data.cumulativeCashflow >= 0 ? "text-green-500" : "text-red-500"}`}>
            {formatCurrency(data.cumulativeCashflow)}
          </p>
          {distanceToBreakeven > 0 && (
            <p className="text-xs text-muted-foreground">
              Nog {formatCurrency(distanceToBreakeven)} tot breakeven
            </p>
          )}
          {data.cumulativeCashflow >= 0 && (
            <p className="text-xs text-green-500">✓ Winst bereikt</p>
          )}
        </div>
      );
    }
    return null;
  };

  const CustomDot = (props: any) => {
    const { cx, cy, payload } = props;
    
    // Render special dot for breakeven year
    if (payload.year === breakevenYear?.year) {
      return (
        <g>
          <circle cx={cx} cy={cy} r={8} fill="hsl(var(--chart-2))" stroke="#fff" strokeWidth={2} />
          <text
            x={cx}
            y={cy - 15}
            textAnchor="middle"
            className="text-xs font-bold fill-foreground"
          >
            BREAKEVEN 🎯
          </text>
        </g>
      );
    }
    
    return null;
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <span>⏱️</span>
          Terugverdientijd
          {paybackPeriod && (
            <span className="ml-auto text-sm font-normal text-muted-foreground">
              Terugverdientijd: {paybackPeriod.toFixed(1)} jaar
            </span>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <ResponsiveContainer width="100%" height={400}>
          <AreaChart data={yearlyData} margin={{ top: 30, right: 30, left: 20, bottom: 5 }}>
            <defs>
              <linearGradient id="colorPositive" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="hsl(var(--chart-2))" stopOpacity={0.8} />
                <stop offset="95%" stopColor="hsl(var(--chart-2))" stopOpacity={0} />
              </linearGradient>
              <linearGradient id="colorNegative" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="hsl(var(--destructive))" stopOpacity={0} />
                <stop offset="100%" stopColor="hsl(var(--destructive))" stopOpacity={0.8} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
            <XAxis
              dataKey="year"
              className="text-xs"
            />
            <YAxis
              tickFormatter={(value) => formatCurrency(value)}
              className="text-xs"
            />
            <Tooltip content={<CustomTooltip />} />
            <ReferenceLine
              y={0}
              stroke="hsl(var(--border))"
              strokeWidth={2}
              strokeDasharray="5 5"
              label={{ value: "Breakeven", position: "right", className: "text-xs fill-muted-foreground" }}
            />
            <Area
              type="monotone"
              dataKey="cumulativeCashflow"
              stroke="hsl(var(--chart-1))"
              strokeWidth={3}
              fill="url(#colorPositive)"
              dot={<CustomDot />}
            />
          </AreaChart>
        </ResponsiveContainer>
        <div className="mt-4 text-center text-sm text-muted-foreground">
          Cumulatieve cashflow over tijd - break-even punt gemarkeerd
        </div>
      </CardContent>
    </Card>
  );
}
